import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { X, Home, Gift, CheckSquare, Image, MessageCircle, MapPin, Settings } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const [session, setSession] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const sidebarVariants = {
    open: { x: 0, transition: { type: "spring", stiffness: 300, damping: 30 } },
    closed: { x: "-100%", transition: { type: "spring", stiffness: 300, damping: 30 } }
  };

  const menuItems = [
    { path: "/", text: "Início", icon: <Home size={20} /> },
    { path: "/presentes", text: "Lista de Presentes", icon: <Gift size={20} /> },
    { path: "/confirmar", text: "Confirmação de Presença", icon: <CheckSquare size={20} /> },
    { path: "/fotos", text: "Álbum de Fotos", icon: <Image size={20} /> },
    { path: "/mensagens", text: "Mensagens aos Noivos", icon: <MessageCircle size={20} /> },
    { path: "/local", text: "Local do Evento", icon: <MapPin size={20} /> },
    // Only show admin link if user is authenticated
    ...(session ? [{ path: "/admin", text: "Painel Admin", icon: <Settings size={20} /> }] : []),
  ];

  return (
    <>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-40 z-40"
          onClick={onClose}
        />
      )}
      
      <motion.div
        className="fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-50 p-6 overflow-y-auto"
        initial="closed"
        animate={isOpen ? "open" : "closed"}
        variants={sidebarVariants}
      >
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-lg font-playfair font-semibold text-wedding-blue-700">Menu</h2>
          <button 
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Fechar menu"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>
        
        <div className="mb-8 text-center">
          <h1 className="font-playfair text-xl font-semibold text-wedding-blue-800 mb-1">Kaisson & Laryssa</h1>
          <p className="text-sm text-wedding-beige-900 italic">Nosso Grande Dia</p>
        </div>
        
        <nav>
          <ul className="space-y-4">
            {menuItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  onClick={onClose}
                  className={({ isActive }) => 
                    `flex items-center px-4 py-3 rounded-lg transition-all duration-200 ${
                      isActive 
                        ? 'bg-wedding-blue-100 text-wedding-blue-800 font-medium' 
                        : 'hover:bg-wedding-beige-100 text-gray-700'
                    }`
                  }
                >
                  <span className="mr-3">{item.icon}</span>
                  <span>{item.text}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        
        <div className="absolute bottom-8 left-0 right-0 text-center text-sm text-gray-500">
          <p>02.08.2025</p>
        </div>
      </motion.div>
    </>
  );
};

export default Sidebar;